import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import CsvFileUploadInput from "components/CsvFileUploadInput";
import TableResultCsvFileUpload from "components/TableResultCsvFileUpload";
import React, { useState, Fragment, useEffect } from "react";
import { Button, useDataProvider } from "react-admin";
import IconPlus from "@material-ui/icons/Add";
import { delay } from "utils/tools";
import TemplateUploadInvoiceButton from "./TemplateUploadInvoiceButton";

export interface IInvoiceFromCsv {
  description: string;
  amount: string;
  dueDate: string;
  parentId: string;
  projectCode: string;
  schoolId: string;
  tbfCode: string;
  type: string;
  status: "failed" | "success" | "pending";
  deferralCode: string;
  deferralStartDate: string;
  subsidyPercentage: string;
  studentId: string;
  message: string;
}
const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
    "& .MuiDialogActions-root": {
      justifyContent: "space-between",
    },
  },
});

const CreateAdhocInvoiceByCsvButton = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [invoices, setInvoices] = useState<IInvoiceFromCsv[]>([]);
  const [results, setResults] = useState<IInvoiceFromCsv[]>([]);
  const dataProvider = useDataProvider();
  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setInvoices([]);
    setResults([]);
  };

  const handleCreateInvoice = (invoice: IInvoiceFromCsv, index: number) => {
    let tempInvoices = invoices;

    // Convert and validate subsidyPercentage
    const rawSubsidy = invoice.subsidyPercentage?.trim();
    let subsidy: number | null = null;

    // Validate subsidyPercentage is empty
    if (rawSubsidy === "") {
      tempInvoices[index].status = "failed";
      tempInvoices[index].message = "Error: Subsidy percentage cannot be empty";
      setResults([...tempInvoices]);
      return;
    }

    if (rawSubsidy !== undefined && rawSubsidy !== null) {
      // Validate subsidyPercentage a valid number
      if (isNaN(Number(rawSubsidy))) {
        tempInvoices[index].status = "failed";
        tempInvoices[index].message = "Error: Subsidy percentage must be a valid number";
        setResults([...tempInvoices]);
        return;
      }

      // Validate subsidyPercentage negative or 0
      subsidy = Number(rawSubsidy);
      if (subsidy <= 0) {
        tempInvoices[index].status = "failed";
        tempInvoices[index].message = "Error: Subsidy percentage cannot be negative or 0";
        setResults([...tempInvoices]);
        return;
      }
    }

    // Convert and validate studentId
    const rawStudentId = invoice.studentId?.trim();
    let studentId: number | null = null;

    // Validate studentId cannot be empty if there is subsidyPercentage
    if (subsidy && !rawStudentId) {
      tempInvoices[index].status = "failed";
      tempInvoices[index].message = "Error: Student ID is required when subsidy percentage is provided";
      setResults([...tempInvoices]);
      return;
    }

    // Validate studentId must be valid number
    if (subsidy && isNaN(Number(rawStudentId))) {
      tempInvoices[index].status = "failed";
      tempInvoices[index].message = "Error: Student ID must be a valid number";
      setResults([...tempInvoices]);
      return;
    }

    // Convert when studentId is valid
    if (subsidy && rawStudentId) {
      studentId = Number(rawStudentId);
    }

    dataProvider
      .create("commercialDocuments", {
        data: {
          dueDate: invoice.dueDate,
          parentId: Number(invoice.parentId),
          studentId: studentId,
          type: invoice.type,
          status: "draft",
          commercialDocumentItem: {
            amount: Math.round(Number(invoice.amount) * 100.0),
            schoolId: Number(invoice.schoolId),
            description: invoice.description,
            tbfCode: invoice.tbfCode,
            projectCode: invoice.projectCode,
            deferralCode:
              invoice.deferralCode === "-" ? null : invoice.deferralCode,
            deferralStartDate:
              invoice.deferralStartDate === "-"
                ? null
                : invoice.deferralStartDate,
            subsidyPercentage: subsidy,
          },
        },
      })
      .then(() => {
        tempInvoices[index].status = "success";
        tempInvoices[index].message = "Success";
        setResults([...tempInvoices]);
      })
      .catch((err) => {
        tempInvoices[index].status = "failed";
        tempInvoices[index].message = `Error: ${err.message}`;
        setResults([...tempInvoices]);
      });
  };

  const fetchInvoice = async (invoices: IInvoiceFromCsv[]) => {
    for (const invoice of invoices) {
      const index = invoices.indexOf(invoice);
      if (invoice.status.toLowerCase() === "pending") {
        await handleCreateInvoice(invoice, index);
        await delay(3500);
      }
    }
  };

  useEffect(() => {
    if (invoices.length > 0) {
      fetchInvoice(invoices);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  return (
    <Fragment>
      <Button
        label="Import Mass Adhoc Invoice"
        onClick={handleOpenDialog}
        style={{ float: "right" }}
      >
        <IconPlus />
      </Button>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          Import Adhoc Invoice by CSV
        </DialogTitle>
        <DialogContent>
          <CsvFileUploadInput
            csvFileType="invoice"
            setValues={(values) => {
              setInvoices(values);
              setResults(values);
            }}
            showErrorForEmptyColumns
          />

          {invoices.length > 0 && (
            <TableResultCsvFileUpload records={results} />
          )}
        </DialogContent>
        <DialogActions>
          <span>
            <TemplateUploadInvoiceButton
              buttonLabel="Download Template Invoice"
              title="Template Import Mass Import Invoice"
              instructions={[
                {
                  text: `1. You can download template for upload mass invoices to system in this link below.`,
                  linkUrl: `https://jane-prod-storage.s3.ap-southeast-1.amazonaws.com/templates/Template+Import+Invoice+V2.xlsx`,
                  linkLabel: `Template Import Invoice V2.xlsx`,
                },
                {
                  text: `2. If doesnt have Deferral Code and Deferral Start Date, can fill in with "-".`,
                },
                {
                  text: (
                    <>
                      3. If the column Description has more than <strong>90 characters</strong>, it will be
                      trimmed because MBC restricts Description length.
                    </>
                  )
                },
                {
                  text: `4. After fill the sheet, export document to CSV format, then upload the document.`
                },
              ]}
            />
            <TemplateUploadInvoiceButton
              buttonLabel="Download Template Invoice with Subsidy"
              title="Template Import Mass Import Invoice with Subsidy"
              instructions={[
                {
                  text: `1. You can download template for upload mass invoices to system in this link below.`,
                  linkUrl: `https://jane-prod-storage.s3.ap-southeast-1.amazonaws.com/templates/Template+Import+Invoice+with+Subsidy.xlsx`,
                  linkLabel: `Template Import Invoice with Subsidy.xlsx`,
                },
                {
                  text: `2. If doesnt have Deferral Code, Deferral Start Date, Subsidy Percentage and External ID, can fill in with "-".`,
                },
                {
                  text: (
                    <>
                      3. If the column Description has more than <strong>90 characters</strong>, it will be
                      trimmed because MBC restricts Description length.
                    </>
                  )
                },
                {
                  text: `4. After fill the sheet, export document to CSV format, then upload the document.`
                },
              ]}
            />
          </span>
          <Button onClick={handleClose} color="secondary" label="Back" />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default CreateAdhocInvoiceByCsvButton;
